import { infiniteQueryOptions, queryOptions } from '@tanstack/react-query';

import { apiFetch } from '@/lib/api-fetch.js';

/**
 * Client for cti-diagram-service (`/v1/diagrams`).
 *
 * The wire is snake_case in both directions and the SPA is camelCase, so every field crossing the
 * boundary is spelled twice — once in a `*Wire` type that matches the service's `diagram-view.ts`
 * exactly, once in the domain type below it — with a mapper between them. That is deliberate
 * duplication: `ValidationPipe({ whitelist: true })` drops a field the DTO does not declare without
 * a word, so a camelCase key sent by mistake is invisible data loss rather than an error. The
 * request builders are covered by tests that assert the body, not the type.
 */

/** The two document kinds the service stores. `bpmn` is deliberately not one of them yet. */
export const DIAGRAM_KINDS = ['mermaid', 'canvas'] as const;
export type DiagramKind = (typeof DIAGRAM_KINDS)[number];

/**
 * A kind this build does not know how to draw. The service is versioned separately and will grow
 * kinds — its design already names `bpmn` — and a deployed SPA can be older than the service
 * answering it. Casting an unknown string to `DiagramKind` would make every non-mermaid document
 * look like a canvas and hand a BPMN payload to Excalidraw, so an unrecognised kind is carried as
 * itself and the screens say plainly that they cannot open it.
 */
export interface UnknownDiagramKind {
  unsupported: string;
}

export type AnyDiagramKind = DiagramKind | UnknownDiagramKind;

export function isSupportedKind(kind: AnyDiagramKind): kind is DiagramKind {
  return typeof kind === 'string';
}

/** What to call a kind on screen, so a document this build cannot open is still listed honestly. */
export function kindLabel(kind: AnyDiagramKind): string {
  if (kind === 'mermaid') return 'Diagram';
  if (kind === 'canvas') return 'Whiteboard';
  return kind.unsupported;
}

/** The kind a wire string names, or the string itself carried as unsupported. */
export function toKind(value: string): AnyDiagramKind {
  const known = DIAGRAM_KINDS.find((candidate) => candidate === value);
  return known ?? { unsupported: value };
}

export const DIAGRAM_CONTENT_FORMAT = 'cti.diagram';
export const DIAGRAM_CONTENT_VERSION = 1;

/** A mermaid document: its source text and nothing else. */
export interface MermaidContent {
  format: typeof DIAGRAM_CONTENT_FORMAT;
  version: number;
  kind: 'mermaid';
  source: string;
}

/**
 * The document-level canvas settings the service persists. Excalidraw's full `appState` is not sent:
 * it mixes document data with one viewer's scroll position and selection, and storing that makes one
 * person's screen into the document.
 */
export interface CanvasSettings {
  viewBackgroundColor?: string;
  gridSize?: number | null;
  gridModeEnabled?: boolean;
}

export interface CanvasContent {
  format: typeof DIAGRAM_CONTENT_FORMAT;
  version: number;
  kind: 'canvas';
  elements: unknown[];
  settings: CanvasSettings;
  files?: Record<string, unknown>;
}

export type DiagramContent = MermaidContent | CanvasContent;

/**
 * Which members of the workspace may see a document. `workspace` is the default and every existing
 * document has it; `group` is the creator plus the members they picked; `private` is the creator
 * alone. A workspace owner who is not included does not see a group or private document either.
 */
export const DIAGRAM_AUDIENCES = ['private', 'group', 'workspace'] as const;
export type DiagramAudience = (typeof DIAGRAM_AUDIENCES)[number];

/**
 * The audience as this build understands it. A service older than the build sends none, which means
 * workspace; a newer one could send a value this build does not know, and that is read as workspace
 * too rather than cast into the union and indexed blindly.
 */
function toAudience(value: string | undefined): DiagramAudience {
  return (
    DIAGRAM_AUDIENCES.find((candidate) => candidate === value) ?? 'workspace'
  );
}

export interface DiagramSummary {
  id: string;
  workspaceId: string;
  kind: AnyDiagramKind;
  title: string;
  revision: number;
  createdByUserId: string;
  updatedByUserId: string;
  /** Set on a canvas made by converting a mermaid document — where it came from, and from which revision. */
  sourceDiagramId: string | null;
  sourceRevision: number | null;
  audience: DiagramAudience;
  /** "Everyone in this workspace can edit". The service applies it only while the audience is Workspace. */
  membersCanEdit: boolean;
  /** Computed by the service for the caller. The screens trust it rather than re-deriving it from a role. */
  canEdit: boolean;
  /** Delete, share outside the workspace and change the audience. Computed by the service. */
  canManage: boolean;
  createdAt: string;
  updatedAt: string;
}

export interface DiagramDetail extends DiagramSummary {
  /** The members a `group` document is shared with. The service fills it only for a caller who manages it. */
  participantIds: string[];
  content: DiagramContent;
}

interface DiagramSummaryWire {
  id: string;
  workspace_id: string;
  kind: string;
  title: string;
  revision: number;
  created_by_user_id: string;
  updated_by_user_id: string;
  source_diagram_id: string | null;
  source_revision: number | null;
  audience?: string;
  members_can_edit?: boolean;
  can_edit?: boolean;
  can_manage?: boolean;
  created_at: string;
  updated_at: string;
}

interface DiagramDetailWire extends DiagramSummaryWire {
  participant_ids?: string[];
  content: DiagramContent;
}

function toSummary(wire: DiagramSummaryWire): DiagramSummary {
  return {
    id: wire.id,
    workspaceId: wire.workspace_id,
    kind: toKind(wire.kind),
    title: wire.title,
    revision: wire.revision,
    createdByUserId: wire.created_by_user_id,
    updatedByUserId: wire.updated_by_user_id,
    sourceDiagramId: wire.source_diagram_id,
    sourceRevision: wire.source_revision,
    audience: toAudience(wire.audience),
    membersCanEdit: wire.members_can_edit === true,
    canEdit: wire.can_edit === true,
    canManage: wire.can_manage === true,
    createdAt: wire.created_at,
    updatedAt: wire.updated_at,
  };
}

function toDetail(wire: DiagramDetailWire): DiagramDetail {
  return {
    ...toSummary(wire),
    participantIds: wire.participant_ids ?? [],
    content: wire.content,
  };
}

const ENDPOINT = '/v1/diagrams';

/**
 * Raised when a write loses the revision race — the document moved between the caller reading it and
 * saving. Carries the revision the server now holds so the editor can say what happened rather than
 * only that something did.
 */
export class DiagramConflictError extends Error {
  readonly currentRevision: number | null;

  constructor(currentRevision: number | null) {
    super('This diagram changed while you were editing it.');
    this.name = 'DiagramConflictError';
    this.currentRevision = currentRevision;
  }
}

/** The service's message if it sent one, so a 422 explains itself instead of showing a status code. */
async function failure(response: Response, fallback: string): Promise<Error> {
  const body = await response.json().catch(() => null);
  const message =
    body &&
    typeof body === 'object' &&
    typeof (body as { message?: unknown }).message === 'string'
      ? (body as { message: string }).message
      : '';
  return new Error(message || `${fallback} (${response.status})`);
}

async function readOrThrow<T>(
  response: Response,
  fallback: string,
): Promise<T> {
  if (response.status === 409) {
    const body = await response.json().catch(() => null);
    const current = (body as { current_revision?: unknown } | null)
      ?.current_revision;
    throw new DiagramConflictError(
      typeof current === 'number' ? current : null,
    );
  }
  if (!response.ok) throw await failure(response, fallback);
  return (await response.json()) as T;
}

export const diagramKeys = {
  all: ['diagrams'] as const,
  list: (workspaceId?: string | null) =>
    ['diagrams', 'list', workspaceId ?? 'none'] as const,
  /** One paged gallery group. It sits under `list`, so whatever invalidates the list refreshes it. */
  page: (workspaceId: string | null, kind: DiagramKind, search: string) =>
    ['diagrams', 'list', workspaceId ?? 'none', 'paged', kind, search] as const,
  detail: (id: string) => ['diagrams', 'detail', id] as const,
  revisions: (id: string) => ['diagrams', 'revisions', id] as const,
  revision: (id: string, revision: number) =>
    ['diagrams', 'revision', id, revision] as const,
  preview: (id: string, revision: number) =>
    ['diagrams', 'preview', id, revision] as const,
};

export async function fetchDiagrams(
  workspaceId: string,
): Promise<DiagramSummary[]> {
  const query = new URLSearchParams({ workspace_id: workspaceId });
  const response = await apiFetch(`${ENDPOINT}?${query.toString()}`);
  const wire = await readOrThrow<DiagramSummaryWire[]>(
    response,
    'Could not load diagrams',
  );
  return wire.map(toSummary);
}

/**
 * The gallery is scoped to the selected workspace and there is no personal fallback, so the query
 * stays disabled until one is chosen rather than asking for every diagram in the platform. It
 * refetches on focus and every minute, so somebody else's new revision reaches an open gallery.
 */
export function diagramsQueryOptions(workspaceId: string | null) {
  return queryOptions({
    queryKey: diagramKeys.list(workspaceId),
    queryFn: () => fetchDiagrams(workspaceId as string),
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

/** How many cards one gallery page asks for, in each group. */
export const DIAGRAM_PAGE_SIZE = 24;

/** What one gallery group shows: one kind, and the titles that contain `q`. An empty `q` is no search. */
export interface DiagramGalleryFilter {
  kind: DiagramKind;
  q: string;
}

/** One page of a gallery group, and the cursor of the page after it, or null when this is the last. */
export interface DiagramPage {
  items: DiagramSummary[];
  nextCursor: string | null;
}

interface DiagramPageWire {
  items: DiagramSummaryWire[];
  next_cursor?: string | null;
}

/**
 * The whole list of a service older than paging, cut down to what the group asked for. That service
 * ignores `kind`, `q` and `limit`, so without this filter both groups would show every document.
 */
function filterWholeList(
  summaries: DiagramSummary[],
  filter: DiagramGalleryFilter,
): DiagramSummary[] {
  const search = filter.q.toLowerCase();
  return summaries.filter(
    (summary) =>
      summary.kind === filter.kind &&
      summary.title.toLowerCase().includes(search),
  );
}

/**
 * One page of a gallery group, newest edit first. `q` is trimmed and left out when empty; `cursor`
 * is the opaque `next_cursor` of the page before. A plain array is the answer of a service older
 * than paging: it is the whole list, so it becomes the only page.
 */
export async function fetchDiagramPage(
  workspaceId: string,
  filter: DiagramGalleryFilter,
  cursor?: string,
): Promise<DiagramPage> {
  const search = filter.q.trim();
  const query = new URLSearchParams({
    workspace_id: workspaceId,
    kind: filter.kind,
    limit: String(DIAGRAM_PAGE_SIZE),
  });
  if (search) query.set('q', search);
  if (cursor !== undefined) query.set('cursor', cursor);
  const response = await apiFetch(`${ENDPOINT}?${query.toString()}`);
  const wire = await readOrThrow<DiagramPageWire | DiagramSummaryWire[]>(
    response,
    'Could not load diagrams',
  );
  if (Array.isArray(wire)) {
    const whole = wire.map(toSummary);
    return {
      items: filterWholeList(whole, { ...filter, q: search }),
      nextCursor: null,
    };
  }
  return {
    items: wire.items.map(toSummary),
    nextCursor: typeof wire.next_cursor === 'string' ? wire.next_cursor : null,
  };
}

/**
 * The cursor of the next page, or undefined to stop: at a null `next_cursor`, and at a cursor this
 * group already used, which would otherwise page the same rows forever.
 */
function nextDiagramCursor(
  lastPage: DiagramPage,
  _allPages: DiagramPage[],
  _lastPageParam: string | undefined,
  allPageParams: (string | undefined)[],
): string | undefined {
  const cursor = lastPage.nextCursor;
  if (cursor === null || allPageParams.includes(cursor)) return undefined;
  return cursor;
}

/**
 * One gallery group, paged 24 at a time. The poll, the focus refetch and every invalidation of
 * `diagramKeys.list` refetch the pages already loaded, from the first, so a group keeps its length
 * through a refresh instead of falling back to one page.
 */
export function diagramsInfiniteQueryOptions(
  workspaceId: string | null,
  filter: DiagramGalleryFilter,
) {
  const search = filter.q.trim();
  return infiniteQueryOptions({
    queryKey: diagramKeys.page(workspaceId, filter.kind, search),
    queryFn: ({ pageParam }) =>
      fetchDiagramPage(
        workspaceId as string,
        { kind: filter.kind, q: search },
        pageParam,
      ),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: nextDiagramCursor,
    enabled: Boolean(workspaceId),
    refetchOnWindowFocus: true,
    refetchInterval: 60_000,
  });
}

export async function fetchDiagram(id: string): Promise<DiagramDetail> {
  const response = await apiFetch(`${ENDPOINT}/${encodeURIComponent(id)}`);
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not load the diagram',
    ),
  );
}

/**
 * The document behind one gallery thumbnail. Keyed by revision, so the cached copy is never stale:
 * a newer revision in the list is a different key and fetches again.
 */
export function diagramPreviewQueryOptions(id: string, revision: number) {
  return queryOptions({
    queryKey: diagramKeys.preview(id, revision),
    queryFn: () => fetchDiagram(id),
    staleTime: Infinity,
  });
}

export function diagramQueryOptions(id: string) {
  return queryOptions({
    queryKey: diagramKeys.detail(id),
    queryFn: () => fetchDiagram(id),
  });
}

export async function createDiagram(input: {
  workspaceId: string;
  kind: DiagramKind;
  title?: string;
  content?: DiagramContent;
  /** Left out, the service stores Workspace. */
  audience?: DiagramAudience;
}): Promise<DiagramDetail> {
  const response = await apiFetch(ENDPOINT, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      workspace_id: input.workspaceId,
      kind: input.kind,
      ...(input.title === undefined ? {} : { title: input.title }),
      ...(input.content === undefined ? {} : { content: input.content }),
      ...(input.audience === undefined ? {} : { audience: input.audience }),
    }),
  });
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not create the diagram',
    ),
  );
}

/**
 * Rename, or overwrite the whole content against the revision the caller read. A content write with a
 * stale revision is refused and nothing is written. The live editors never overwrite content this
 * way — they edit through the collaboration socket — so the editor only renames here, and a rename
 * needs no revision: a title is last-writer-wins, and requiring one would refuse every rename made
 * while somebody is drawing.
 */
export async function patchDiagram(input: {
  id: string;
  expectedRevision?: number;
  title?: string;
  content?: DiagramContent;
}): Promise<DiagramDetail> {
  const response = await apiFetch(
    `${ENDPOINT}/${encodeURIComponent(input.id)}`,
    {
      method: 'PATCH',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        ...(input.expectedRevision === undefined
          ? {}
          : { expected_revision: input.expectedRevision }),
        ...(input.title === undefined ? {} : { title: input.title }),
        ...(input.content === undefined ? {} : { content: input.content }),
      }),
    },
  );
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not save the diagram',
    ),
  );
}

/**
 * Move a document between Private, Group and Workspace, and switch "Everyone in this workspace can
 * edit". Needs the manage right, and carries no revision: who may see or edit a drawing is not part
 * of its content, so it cannot lose a race with an edit. `participant_ids` and `members_can_edit` go
 * out only when the caller passes them.
 */
export async function updateDiagramAudience(
  id: string,
  change: {
    audience: DiagramAudience;
    participantIds?: string[];
    membersCanEdit?: boolean;
  },
): Promise<DiagramDetail> {
  const response = await apiFetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({
      audience: change.audience,
      ...(change.participantIds === undefined
        ? {}
        : { participant_ids: change.participantIds }),
      ...(change.membersCanEdit === undefined
        ? {}
        : { members_can_edit: change.membersCanEdit }),
    }),
  });
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not change who can see the diagram',
    ),
  );
}

/**
 * Make a NEW whiteboard from a Mermaid diagram. `expectedRevision` is the revision the conversion was
 * made from, so the whiteboard records exactly which version it came from; a stale one answers 409
 * and nothing is created. The original diagram is not changed.
 */
export async function convertDiagram(input: {
  id: string;
  expectedRevision: number;
  title?: string;
  content: CanvasContent;
}): Promise<DiagramDetail> {
  const response = await apiFetch(
    `${ENDPOINT}/${encodeURIComponent(input.id)}/convert`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expected_revision: input.expectedRevision,
        to: 'canvas',
        content: input.content,
        ...(input.title === undefined ? {} : { title: input.title }),
      }),
    },
  );
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not create the whiteboard',
    ),
  );
}

/** One entry of a diagram's history, as `GET /v1/diagrams/:id/revisions` lists it. */
export interface DiagramRevision {
  revision: number;
  kind: AnyDiagramKind;
  authorUserId: string;
  /** What the service says happened, such as `created`, `updated` or `restored from revision 2`. */
  summary: string;
  createdAt: string;
}

/** One earlier revision with its scene, for a preview before a restore. */
export interface DiagramRevisionWithContent extends DiagramRevision {
  content: DiagramContent;
}

interface DiagramRevisionWire {
  revision: number;
  kind: string;
  checksum: string;
  author_user_id: string;
  summary: string;
  created_at: string;
}

interface DiagramRevisionContentWire extends DiagramRevisionWire {
  content: DiagramContent;
}

function toRevision(wire: DiagramRevisionWire): DiagramRevision {
  return {
    revision: wire.revision,
    kind: toKind(wire.kind),
    authorUserId: wire.author_user_id,
    summary: wire.summary,
    createdAt: wire.created_at,
  };
}

/** How many revisions one page of the history asks for. */
export const REVISION_PAGE_SIZE = 100;

/**
 * One page of a diagram's history, newest first: the newest revisions, or with `before` the ones
 * older than it. The list carries no scenes; one is read on demand.
 */
export async function fetchDiagramRevisions(
  id: string,
  before?: number,
): Promise<DiagramRevision[]> {
  const query = new URLSearchParams({ limit: String(REVISION_PAGE_SIZE) });
  if (before !== undefined) query.set('before', String(before));
  const response = await apiFetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/revisions?${query}`,
  );
  const wire = await readOrThrow<DiagramRevisionWire[]>(
    response,
    'Could not load the history',
  );
  return wire.map(toRevision);
}

export async function fetchDiagramRevision(
  id: string,
  revision: number,
): Promise<DiagramRevisionWithContent> {
  const response = await apiFetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/revisions/${revision}`,
  );
  const wire = await readOrThrow<DiagramRevisionContentWire>(
    response,
    'Could not load this revision',
  );
  return { ...toRevision(wire), content: wire.content };
}

/**
 * Make an earlier revision the head again. `expectedRevision` is the head the caller saw; a document
 * that moved since answers 409 and nothing changes. The service applies the restore as a diff onto
 * the live document, so every open editor receives it without a reload.
 */
export async function restoreDiagramRevision(
  id: string,
  input: { expectedRevision: number; revision: number },
): Promise<DiagramDetail> {
  const response = await apiFetch(
    `${ENDPOINT}/${encodeURIComponent(id)}/restore`,
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        expected_revision: input.expectedRevision,
        revision: input.revision,
      }),
    },
  );
  return toDetail(
    await readOrThrow<DiagramDetailWire>(
      response,
      'Could not restore this revision',
    ),
  );
}

export async function deleteDiagram(id: string): Promise<void> {
  const response = await apiFetch(`${ENDPOINT}/${encodeURIComponent(id)}`, {
    method: 'DELETE',
  });
  if (!response.ok)
    throw await failure(response, 'Could not delete the diagram');
}

/** An empty document of the kind, matching what the service would have stored for one. */
export function emptyDiagramContent(kind: DiagramKind): DiagramContent {
  if (kind === 'mermaid') {
    return {
      format: DIAGRAM_CONTENT_FORMAT,
      version: DIAGRAM_CONTENT_VERSION,
      kind: 'mermaid',
      source: '',
    };
  }
  return {
    format: DIAGRAM_CONTENT_FORMAT,
    version: DIAGRAM_CONTENT_VERSION,
    kind: 'canvas',
    elements: [],
    settings: {},
  };
}
